import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import { Delete, Filter } from "lucide-react";
import FilterField from "./CustomMultiSelect";
import { CATEGORY_DATA } from "../../enums/categoryData";

const allCategories = Object.entries(CATEGORY_DATA)
  .map(([code, item]) => `${item.category} - ${code}`)
  .sort();

function FilterModal({
  open,
  onClose,
  onApply,
  initialFilters = {},
  catalogCategory,
  filterOptions = {},
}) {
  const [filters, setFilters] = useState(initialFilters);

  // Keep only filters that are in the current config
  const filteredFilters = Object.fromEntries(
    Object.entries(filters).filter(([key]) => filterOptions[key])
  );

  const appliedFilterKeys = Object.keys(filteredFilters);
  const availableFilterKeys = Object.keys(filterOptions).filter(
    (key) => !appliedFilterKeys.includes(key)
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleApply = () => {
    const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      const hasValue = value !== "" && value !== null && value !== undefined && (!(Array.isArray(value)) || value.length > 0);
      if (!hasValue) return acc;

      if (key === "category" && filterOptions[key]?.label === "Category") {
        const codes = value.map((v) => v.split(" - ").pop());
        acc["sku_pref"] = codes;
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});

    onApply?.(cleanedFilters);
    onClose();
  };

  const handleClearAll = () => setFilters({});

  const renderFilterInput = (key, value) => {
    const config = filterOptions[key] || {};
    if (config.type === "string") {
      return (
        <TextField
          label={config.label}
          value={value || ""}
          onChange={(e) => handleFilterChange(key, e.target.value)}
          fullWidth
          autoComplete="off"
        />
      );
    }

    return (
      <FilterField
        label={config.label}
        options={config.options}
        value={value}
        onChange={(newValue) => handleFilterChange(key, newValue)}
      />
    );
  };

  const activeFiltersCount = Object.values(filters).filter((value) =>
    Array.isArray(value) ? value.length > 0 : value !== "" && value !== null && value !== undefined
  ).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Filter size={20} />
          <Typography variant="h6">Filters</Typography>
          {activeFiltersCount > 0 && (
            <Chip label={`${activeFiltersCount} active`} size="small" color="primary" />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {availableFilterKeys.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                Quick Add:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {availableFilterKeys.map((key) => {
                  const config = filterOptions[key] || {};
                  return (
                    <Chip
                      key={key}
                      icon={config.icon}
                      label={config.label}
                      variant="outlined"
                      clickable
                      onClick={() => setFilters((prev) => ({ ...prev, [key]: config.type === "string" ? "" : [] }))}
                      sx={{
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.contrastText",
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}

          {appliedFilterKeys.length > 0 && (
            <>
              {availableFilterKeys.length > 0 && <Divider />}
              <Stack spacing={2}>
                {appliedFilterKeys.map((key) => {
                  const config = filterOptions[key] || {};
                  return (
                    <Card key={key} variant="outlined" sx={{ backgroundColor: "grey.50" }}>
                      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          {config.icon}
                          <Typography variant="subtitle2">{config.label}</Typography>
                          <IconButton size="small" onClick={() => handleRemoveFilter(key)} sx={{ ml: "auto" }}>
                            <Delete size={16} />
                          </IconButton>
                        </Box>
                        {renderFilterInput(key, filteredFilters[key])}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </>
          )}

          {appliedFilterKeys.length === 0 && (
            <Box textAlign="center" py={3}>
              <Filter size={48} style={{ opacity: 0.3, marginBottom: 8 }} />
              <Typography variant="body2" color="text.secondary">
                No filters applied. Click any filter above to get started.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        {activeFiltersCount > 0 && (
          <Button onClick={handleClearAll} color="error" variant="outlined">
            Clear All
          </Button>
        )}
        <Button onClick={handleApply} variant="contained" disabled={activeFiltersCount === 0}>
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FilterModal;
